export interface FileReport {
  fileId: number;
  fileName: string;
  fileCaption: string;
  fileCategory: string;
  fileDate: string;
}

/**
 * A file report as REPORTS_FILES_ADMIN users get it (`GET /reports/files/manage`): the
 * FileReport fields plus what the edit dialog and the per-file menu need.
 */
export interface ManagedFileReport extends FileReport {
  pageId: number;
  categoryId: number;
  priority: number;
  annotation: string | null;
  visible: boolean;
  updatedDate: string;
  updatedBy: string | null;
}

export const isManagedFileReport = (file: FileReport): file is ManagedFileReport =>
  'pageId' in file;

/** A category the admin form can file a report under (`GET /reports/files/category-options`). */
export interface FileReportCategoryOption {
  id: number;
  pageTypeName: string;
  /** "Parent => Name", as in the legacy admin dropdown */
  label: string;
  /** Pools that can see the category; empty = every user with File Reports access */
  pools: string[];
}

export interface FileReportFormValues {
  pageCaption: string;
  categoryId: number;
  /** yyyy-MM-dd */
  pageDate: string;
  pagePriority: number;
  reportAnnotation: string;
  visible: boolean;
}

export interface FileReportsResponse {
  data: FileReport[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface FileReportsFilters {
  search?: string;
  category?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Admin list only: include hidden files */
  includeHidden?: boolean;
}

export interface FileReportsPagination {
  pageNumber: number;
  pageSize: number;
  dateOrder: 'asc' | 'desc';
}

export interface FileReportsState {
  files: FileReport[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  filters: FileReportsFilters;
  pagination: FileReportsPagination;
  hasNextPage: boolean;
  totalCount: number;
  viewMode: 'grid' | 'list';
  categories: string[];
  categoryOptions: FileReportCategoryOption[];
}

export type FileType = 'pdf' | 'xlsx' | 'docx' | 'txt' | 'csv' | 'unknown';

export interface DownloadModalProps {
  file: FileReport | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (file: FileReport) => Promise<void>;
}
